import { linearGradientDef } from '@nivo/core'
import { ResponsiveLine } from '@nivo/line'
import moment from 'moment'
import type React from 'react'
import type { OrdersPerDayDto } from '../../../../../../types/coreApi-types'
import { formatNumber } from '../../../../../../utils/formatters'

interface OrdersPerDayChartProps {
  data: OrdersPerDayDto[]
}

const OrdersPerDayChart: React.FC<OrdersPerDayChartProps> = ({ data }) => {
  const dataLength = data.length
  // Determine step size for showing x-axis labels (every nth tick)
  // For longer timeframes, show fewer labels to avoid bunching
  const getStepSize = () => {
    if (dataLength > 60) return 5 // Show every 5th tick
    if (dataLength > 30) return 3 // Show every 3rd tick
    if (dataLength > 15) return 2 // Show every 2nd tick
    return 1 // Show all ticks
  }

  const stepSize = getStepSize()

  const chartData = [
    {
      id: 'orders',
      color: 'hsl(210, 70%, 50%)',
      data: data.map((item, index) => ({
        x: moment(item.date).format('MMM DD'),
        y: item.orderCount,
        volume: item.totalVolume,
        index, // Store index for filtering
      })),
    },
  ]

  return (
    <div className="h-[400px]">
      <ResponsiveLine
        data={chartData}
        margin={{
          top: 50,
          right: 110,
          bottom: dataLength > 30 ? 80 : 50,
          left: 60,
        }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 0,
          max: 'auto',
          stacked: false,
          reverse: false,
        }}
        yFormat=" >-.0f"
        curve="monotoneX"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
          legend: 'Date',
          legendOffset: dataLength > 30 ? 60 : 45,
          legendPosition: 'middle',
          format: value => {
            // Find the index of this value in the data
            const dataPoint = chartData[0].data.find(d => d.x === value)
            if (!dataPoint) return value
            // Only show label if index matches step size
            return (dataPoint.index as number) % stepSize === 0 ? value : ''
          },
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Order Count',
          legendOffset: -40,
          legendPosition: 'middle',
        }}
        pointSize={6}
        pointColor={{ theme: 'background' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'serieColor' }}
        pointLabelYOffset={-12}
        enableArea={true}
        areaBaselineValue={0}
        defs={[
          linearGradientDef('gradient', [
            { offset: 0, color: 'inherit' },
            { offset: 100, color: 'inherit', opacity: 0 },
          ]),
        ]}
        fill={[{ match: '*', id: 'gradient' }]}
        useMesh={true}
        tooltip={({ point }) => {
          const dataPoint = chartData[0].data.find(d => d.x === point.data.x)
          const volume = dataPoint?.volume as number | undefined
          return (
            <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
              <div className="font-semibold">{point.data.xFormatted}</div>
              <div className="text-sm text-gray-600">
                Orders: {point.data.yFormatted}
              </div>
              {volume !== undefined && (
                <div className="text-sm text-gray-600">
                  Volume:{' '}
                  {formatNumber(volume, {
                    currencyType: 'FiatCurrency',
                    isoCode: 'EUR',
                  })}
                </div>
              )}
            </div>
          )
        }}
        colors={{ scheme: 'nivo' }}
        legends={[
          {
            anchor: 'bottom-right',
            direction: 'column',
            justify: false,
            translateX: 100,
            translateY: 0,
            itemsSpacing: 0,
            itemDirection: 'left-to-right',
            itemWidth: 80,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: 'circle',
          },
        ]}
      />
    </div>
  )
}

export default OrdersPerDayChart
